import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useTheme } from "../context/ThemeContext";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./about.css";

export default function About() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <>
      <Navbar />
      <main className={`about-page ${isDark ? 'about-dark' : 'about-light'}`}>
        <Container className="py-5">
          {/* Header Section */}
          <div className="text-center mb-5">
            <div className="about-badge mb-3">
              <span className="badge bg-primary px-3 py-2 rounded-pill">ABOUT US</span>
            </div>
            <h1 className={`display-4 fw-bold mb-4 ${isDark ? 'text-white' : 'text-dark'}`}>
              Revolutionizing Energy Assessment
            </h1>
            <p className={`fs-5 ${isDark ? 'text-white-50' : 'text-muted'} mb-5`}>
              EPC Spark is the future of energy performance certification, designed exclusively for assessors who demand precision, efficiency, and compliance.
            </p>
          </div>

          {/* Mission Section */}
          <Row className="mb-5 align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className={`mission-card p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                <h2 className={`h3 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>Our Mission</h2>
                <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                  To empower energy assessors with cutting-edge AI technology that streamlines every aspect of their workflow, from initial site visits to final report generation. We believe that technology should enhance human expertise, not replace it.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className={`vision-card p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                <h2 className={`h3 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>Our Vision</h2>
                <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                  To become the leading platform that transforms how energy assessments are conducted, making the process more accurate, efficient, and accessible while contributing to a more sustainable future.
                </p>
              </div>
            </Col>
          </Row>

          {/* Story Section */}
          <Row className="mb-5">
            <Col lg={12}>
              <div className={`story-section p-5 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                <h2 className={`h2 mb-4 text-center ${isDark ? 'text-white' : 'text-dark'}`}>Our Story</h2>
                <Row>
                  <Col md={6} className="mb-4">
                    <h3 className={`h4 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>The Beginning</h3>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Founded by a team of energy assessors and software engineers, EPC Spark was born from the frustration of using outdated, inefficient tools that slowed down the assessment process and increased the risk of errors.
                    </p>
                  </Col>
                  <Col md={6} className="mb-4">
                    <h3 className={`h4 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>The Innovation</h3>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      We combined deep industry knowledge with cutting-edge AI technology to create a platform that understands the unique challenges of energy assessment and provides intelligent solutions.
                    </p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Features Section */}
          <Row className="mb-5">
            <Col lg={12}>
              <h2 className={`h2 mb-4 text-center ${isDark ? 'text-white' : 'text-dark'}`}>Why Choose EPC Spark?</h2>
              <Row>
                <Col md={4} className="mb-4">
                  <div className={`feature-card p-4 rounded-3 text-center ${isDark ? 'bg-dark' : 'bg-light'}`}>
                    <div className={`feature-icon mb-3 ${isDark ? 'text-primary' : 'text-primary'}`}>
                      <i className="bi bi-lightning-charge fs-1"></i>
                    </div>
                    <h3 className={`h5 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>AI-Powered Efficiency</h3>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Advanced AI algorithms that learn from your workflow and provide intelligent suggestions to speed up assessments.
                    </p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className={`feature-card p-4 rounded-3 text-center ${isDark ? 'bg-dark' : 'bg-light'}`}>
                    <div className={`feature-icon mb-3 ${isDark ? 'text-primary' : 'text-primary'}`}>
                      <i className="bi bi-shield-check fs-1"></i>
                    </div>
                    <h3 className={`h5 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>Compliance First</h3>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Built with regulatory compliance in mind, ensuring your assessments meet all current standards and requirements.
                    </p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className={`feature-card p-4 rounded-3 text-center ${isDark ? 'bg-dark' : 'bg-light'}`}>
                    <div className={`feature-icon mb-3 ${isDark ? 'text-primary' : 'text-primary'}`}>
                      <i className="bi bi-people fs-1"></i>
                    </div>
                    <h3 className={`h5 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>Assessor-Focused</h3>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Designed by assessors, for assessors. Every feature is crafted to address real-world challenges you face daily.
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Team Section */}
          <Row className="mb-5">
            <Col lg={12}>
              <h2 className={`h2 mb-4 text-center ${isDark ? 'text-white' : 'text-dark'}`}>Our Team</h2>
              <Row>
                <Col md={6} className="mb-4">
                  <div className={`team-card p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                    <div className="d-flex align-items-center mb-3">
                      <div className={`team-avatar me-3 ${isDark ? 'bg-primary' : 'bg-primary'}`}>
                        <i className="bi bi-person text-white"></i>
                      </div>
                      <div>
                        <h3 className={`h5 mb-1 ${isDark ? 'text-white' : 'text-dark'}`}>Energy Assessment Experts</h3>
                        <p className={`small ${isDark ? 'text-white-50' : 'text-muted'}`}>Industry Professionals</p>
                      </div>
                    </div>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Our team includes certified energy assessors with decades of combined experience in the field.
                    </p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className={`team-card p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                    <div className="d-flex align-items-center mb-3">
                      <div className={`team-avatar me-3 ${isDark ? 'bg-primary' : 'bg-primary'}`}>
                        <i className="bi bi-code-slash text-white"></i>
                      </div>
                      <div>
                        <h3 className={`h5 mb-1 ${isDark ? 'text-white' : 'text-dark'}`}>Technology Innovators</h3>
                        <p className={`small ${isDark ? 'text-white-50' : 'text-muted'}`}>Software Engineers</p>
                      </div>
                    </div>
                    <p className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      Expert developers specializing in AI, machine learning, and user experience design.
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Contact CTA */}
          <Row>
            <Col lg={12}>
              <div className={`cta-section p-5 rounded-3 text-center ${isDark ? 'bg-dark' : 'bg-light'}`}>
                <h2 className={`h3 mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>Ready to Transform Your Workflow?</h2>
                <p className={`mb-4 ${isDark ? 'text-white-50' : 'text-muted'}`}>
                  Join thousands of assessors who have already upgraded to EPC Spark and are experiencing the future of energy assessment.
                </p>
                <a href="/contact" className="btn btn-primary btn-lg me-3">
                  Get Started Today
                </a>
                <a href="/pricing" className="btn btn-outline-primary btn-lg">
                  View Pricing
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </main>
      <Footer />
    </>
  );
} 